/**
 * CreateRecipeDialog
 *
 * Three-step modal for turning a recipe generated from a monitoring run
 * (`generateRecipeFromRun`) into a shareable .recipe.json:
 *
 *   1. Metadata — name, description, category, tags, author
 *   2. Review & sanitize — full recipe JSON in an editor so the user can
 *      remove anything sensitive the auto-scrubber missed
 *   3. Download — save the file, with instructions to test it via the
 *      gallery "Import from JSON" card and contribute it via PR
 *
 * Follows the gallery's view-swap pattern: one Dialog, three internal views
 * with back/next navigation and state preserved between steps.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Checkbox,
  Input,
  Textarea,
  Field,
  Select,
  Dialog,
  DialogSurface,
  DialogBody,
} from '@fluentui/react-components';
import {
  ArrowDownload16Regular,
  CheckmarkCircle16Regular,
  Dismiss24Regular,
  ErrorCircle16Regular,
  ShieldCheckmark16Regular,
} from '@fluentui/react-icons';
import { validateRecipe, type Recipe } from '@/types/recipe';
import { TEMPLATE_CATEGORIES, CATEGORY_DISPLAY } from '@/types/template';
import { useNotificationStore } from '@/stores/notificationStore';
import { telemetryService } from '@/services/telemetryService';
import { thinScrollbarStyles } from '@/styles/scrollbar';
import { JsonEditor } from '@/components/WorkflowDesigner/JsonEditor/JsonEditor';
import { ConnectorIcon } from '@/components/ConnectionsView/ConnectorIcon';
import { useConnectorMetadata } from '@/hooks/useConnectionMutations';

// Public GitHub repo recipe contributions are PR'd to. New recipes are added
// as recipes/<id>/recipe.json and listed in recipes/manifest.json there.
const PUBLIC_RECIPES_REPO_URL = 'https://github.com/Azure/ProjectAutoRecipes';

// Recipe featuredConnectors entries require a non-empty iconUrl; used when
// connector metadata yields no icon (e.g. built-in connection providers).
const FALLBACK_CONNECTOR_ICON = '/connectorIcons/http.svg';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  surface: {
    width: '760px',
    maxWidth: '90vw',
    maxHeight: '85vh',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    padding: 0,
    gap: 0,
    rowGap: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    flexShrink: 0,
  },
  headerTitle: {
    flex: 1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  stepLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  content: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    overflowY: 'auto',
    ...thinScrollbarStyles,
  },
  hint: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  infoBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  infoTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  infoText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  // Fixed-height editor that scrolls internally, so the surrounding step
  // body (info box + editor + validation errors) can scroll as a whole when
  // it exceeds the dialog height.
  editorWrapper: {
    height: '45vh',
    minHeight: '240px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    backgroundColor: tokens.colorPaletteRedBackground1,
    maxHeight: '100px',
    overflowY: 'auto',
    flexShrink: 0,
    ...thinScrollbarStyles,
  },
  errorTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  errorItem: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    fontFamily: tokens.fontFamilyMonospace,
  },
  connectorList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  connectorLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  downloadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  fileName: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  downloadedNote: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteGreenForeground1,
  },
  nextSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  nextStepsList: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase400,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    flexShrink: 0,
  },
});

// =============================================================================
// HELPERS
// =============================================================================

type Step = 'metadata' | 'review' | 'download';

const STEP_INFO: Record<Step, { index: number; title: string }> = {
  metadata: { index: 1, title: 'Describe your recipe' },
  review: { index: 2, title: 'Review & remove sensitive data' },
  download: { index: 3, title: 'Download' },
};

/** Convert a recipe name to a URL-safe kebab-case id (same rule as recipeFromRun). */
function toKebabCase(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
}

/** Heuristic display name from an apiId (same rule as ConnectorName). */
function connectorNameFromApiId(apiId: string): string {
  const match = apiId.match(/managedApis\/([^/]+)$/i);
  if (match?.[1]) {
    const name = match[1];
    return name.charAt(0).toUpperCase() + name.slice(1);
  }
  const segments = apiId.split('/');
  return segments[segments.length - 1] || apiId;
}

/** Unique connector apiIds referenced by the recipe's connections. */
function uniqueConnectionApiIds(recipe: Recipe): string[] {
  return [...new Set(Object.values(recipe.connections).map((c) => c.apiId))];
}

type ResolvedConnectorMeta = { displayName: string; iconUrl: string };

interface MetadataFields {
  name: string;
  description: string;
  category: string;
  tags: string;
  author: string;
}

function metadataFieldsFromRecipe(recipe: Recipe): MetadataFields {
  return {
    name: recipe.metadata.name,
    description: recipe.metadata.description,
    category: recipe.metadata.category[0] ?? 'automation',
    tags: recipe.metadata.tags.join(', '),
    author: recipe.metadata.author,
  };
}

/**
 * Merge the step-1 fields back into the recipe's metadata block and replace
 * `featuredConnectors` with the user's checkbox selection (omitted entirely
 * when nothing is selected — the field is optional in the schema).
 */
function applyMetadata(
  recipe: Recipe,
  fields: MetadataFields,
  featuredConnectors: NonNullable<Recipe['featuredConnectors']>,
): Recipe {
  const name = fields.name.trim();
  const { featuredConnectors: _previous, ...rest } = recipe;
  return {
    ...rest,
    ...(featuredConnectors.length > 0 ? { featuredConnectors } : {}),
    metadata: {
      ...recipe.metadata,
      id: toKebabCase(name),
      name,
      description: fields.description.trim(),
      category: [fields.category],
      tags: fields.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      author: fields.author.trim(),
    },
  };
}

// =============================================================================
// FEATURED-CONNECTOR CHECKBOX
// =============================================================================

/**
 * One checkbox per connector referenced by the recipe's connections. Resolves
 * the connector's display name and icon from connector metadata (same source
 * as the Connections dialog) and reports the resolved values up so the parent
 * can embed them in the generated `featuredConnectors` entries.
 */
const FeaturedConnectorCheckbox: React.FC<{
  apiId: string;
  checked: boolean;
  onToggle: (apiId: string, checked: boolean) => void;
  onResolved: (apiId: string, meta: ResolvedConnectorMeta) => void;
}> = ({ apiId, checked, onToggle, onResolved }) => {
  const styles = useStyles();
  const { data: connector } = useConnectorMetadata(apiId);

  const displayName =
    connector?.displayName ?? connector?.name ?? connectorNameFromApiId(apiId);
  const iconUrl = connector?.iconUri ?? '';

  useEffect(() => {
    if (connector) {
      onResolved(apiId, { displayName, iconUrl });
    }
  }, [apiId, connector, displayName, iconUrl, onResolved]);

  return (
    <Checkbox
      checked={checked}
      onChange={(_, data) => onToggle(apiId, data.checked === true)}
      label={{
        children: (
          <span className={styles.connectorLabel}>
            <ConnectorIcon {...(iconUrl ? { iconUri: iconUrl } : {})} size={16} />
            {displayName}
          </span>
        ),
      }}
    />
  );
};

// =============================================================================
// PROPS
// =============================================================================

export interface CreateRecipeDialogProps {
  open: boolean;
  /** The recipe generated from the run (already auto-sanitized + validated). */
  recipe: Recipe | null;
  onDismiss: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const CreateRecipeDialog: React.FC<CreateRecipeDialogProps> = ({
  open,
  recipe,
  onDismiss,
}) => {
  const styles = useStyles();

  const [step, setStep] = useState<Step>('metadata');
  const [fields, setFields] = useState<MetadataFields>({
    name: '',
    description: '',
    category: 'automation',
    tags: '',
    author: '',
  });
  // The recipe the metadata step builds on. Starts as the generated recipe and
  // absorbs the user's JSON edits when they navigate back from the review step,
  // so back/forward round-trips don't discard edits.
  const [workingRecipe, setWorkingRecipe] = useState<Recipe | null>(null);
  // Featured-connector checkboxes: apiId → checked. Absent = checked (default).
  const [featuredSelection, setFeaturedSelection] = useState<Record<string, boolean>>({});
  // Metadata resolved by the checkbox rows (display name + icon per apiId).
  const [resolvedConnectorMeta, setResolvedConnectorMeta] = useState<
    Record<string, ResolvedConnectorMeta>
  >({});
  const [jsonText, setJsonText] = useState('');
  const [jsonErrors, setJsonErrors] = useState<string[]>([]);
  const [finalRecipe, setFinalRecipe] = useState<Recipe | null>(null);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  // (Re)initialize when a freshly generated recipe arrives.
  useEffect(() => {
    if (open && recipe) {
      setStep('metadata');
      setFields(metadataFieldsFromRecipe(recipe));
      setWorkingRecipe(recipe);
      setFeaturedSelection({});
      // Seed resolved metadata from any featuredConnectors the recipe already
      // carries; live connector metadata overrides these as it loads.
      setResolvedConnectorMeta(
        Object.fromEntries(
          (recipe.featuredConnectors ?? []).map((fc) => [
            fc.id,
            { displayName: fc.displayName, iconUrl: fc.iconUrl },
          ]),
        ),
      );
      setJsonText('');
      setJsonErrors([]);
      setFinalRecipe(null);
      setHasDownloaded(false);
      telemetryService.trackEvent('template.generate_opened', {
        recipeId: recipe.metadata.id,
      });
    }
  }, [open, recipe]);

  const connectorApiIds = useMemo(
    () => (workingRecipe ? uniqueConnectionApiIds(workingRecipe) : []),
    [workingRecipe],
  );

  const handleConnectorToggle = useCallback((apiId: string, checked: boolean) => {
    setFeaturedSelection((prev) => ({ ...prev, [apiId]: checked }));
  }, []);

  const handleConnectorResolved = useCallback((apiId: string, meta: ResolvedConnectorMeta) => {
    setResolvedConnectorMeta((prev) => {
      const existing = prev[apiId];
      if (existing && existing.displayName === meta.displayName && existing.iconUrl === meta.iconUrl) {
        return prev;
      }
      return { ...prev, [apiId]: meta };
    });
  }, []);

  const setField = useCallback(<K extends keyof MetadataFields>(key: K, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  const isMetadataValid =
    fields.name.trim().length > 0 &&
    fields.description.trim().length > 0 &&
    fields.author.trim().length > 0;

  // Step 1 → 2: fold the metadata fields and the featured-connector selection
  // into the recipe and serialize it for review. The JSON becomes the single
  // source of truth from here on.
  const handleMetadataNext = useCallback(() => {
    if (!workingRecipe || !isMetadataValid) return;
    const featuredConnectors = connectorApiIds
      .filter((apiId) => featuredSelection[apiId] !== false)
      .map((apiId) => ({
        id: apiId,
        displayName:
          resolvedConnectorMeta[apiId]?.displayName || connectorNameFromApiId(apiId),
        iconUrl: resolvedConnectorMeta[apiId]?.iconUrl || FALLBACK_CONNECTOR_ICON,
      }));
    setJsonText(
      JSON.stringify(applyMetadata(workingRecipe, fields, featuredConnectors), null, 2),
    );
    setJsonErrors([]);
    setStep('review');
  }, [workingRecipe, fields, isMetadataValid, connectorApiIds, featuredSelection, resolvedConnectorMeta]);

  // Step 2 → 1: carry any JSON edits back into the form when they parse —
  // metadata fields, the working recipe itself, and the checkbox states
  // (checked = connector still present in the JSON's featuredConnectors).
  const handleReviewBack = useCallback(() => {
    const result = validateRecipe((() => {
      try {
        return JSON.parse(jsonText) as unknown;
      } catch {
        return null;
      }
    })());
    if (result.success) {
      setWorkingRecipe(result.data);
      setFields(metadataFieldsFromRecipe(result.data));
      const featuredIds = new Set((result.data.featuredConnectors ?? []).map((fc) => fc.id));
      setFeaturedSelection(
        Object.fromEntries(
          uniqueConnectionApiIds(result.data).map((apiId) => [apiId, featuredIds.has(apiId)]),
        ),
      );
    }
    setStep('metadata');
  }, [jsonText]);

  const handleJsonChange = useCallback((value: string) => {
    setJsonText(value);
    setJsonErrors([]);
  }, []);

  // Step 2 → 3: gate on JSON parse + full recipe schema validation.
  const handleReviewNext = useCallback(() => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonText);
    } catch (err) {
      setJsonErrors([`Not valid JSON: ${err instanceof Error ? err.message : String(err)}`]);
      return;
    }
    const result = validateRecipe(parsed);
    if (!result.success) {
      setJsonErrors(result.errors);
      return;
    }
    setFinalRecipe(result.data);
    setStep('download');
  }, [jsonText]);

  const fileName = `${finalRecipe?.metadata.id ?? 'recipe'}.recipe.json`;

  const handleDownload = useCallback(() => {
    if (!finalRecipe) return;
    const blob = new Blob([JSON.stringify(finalRecipe, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setHasDownloaded(true);
    telemetryService.trackEvent('template.generate_downloaded', {
      recipeId: finalRecipe.metadata.id,
    });
    useNotificationStore.getState().addNotification({
      type: 'success',
      title: 'Recipe downloaded',
      message: `"${finalRecipe.metadata.name}" has been saved as ${fileName}.`,
    });
  }, [finalRecipe, fileName]);

  const handleOpenChange = useCallback(
    (_: unknown, data: { open: boolean }) => {
      if (!data.open) onDismiss();
    },
    [onDismiss]
  );

  if (!recipe) return null;

  const stepInfo = STEP_INFO[step];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modalType="modal">
      <DialogSurface className={styles.surface} aria-label="Create recipe from run">
        <DialogBody className={styles.body}>
          {/* Header */}
          <div className={styles.header}>
            <Text as="h2" className={styles.headerTitle}>
              {stepInfo.title}
            </Text>
            <Text className={styles.stepLabel}>Step {stepInfo.index} of 3</Text>
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={onDismiss}
              aria-label="Close create recipe dialog"
            />
          </div>

          {/* Step 1 — Metadata */}
          {step === 'metadata' && (
            <>
              <div className={styles.content}>
                <Text className={styles.hint}>
                  This recipe was generated from the selected run. Give it a name and
                  description so others can find and understand it.
                </Text>
                <Field label="Name" required>
                  <Input
                    value={fields.name}
                    onChange={(_, data) => setField('name', data.value)}
                    placeholder="e.g. Send weekly status email"
                    aria-label="Recipe name"
                  />
                </Field>
                <Field label="Description" required>
                  <Textarea
                    value={fields.description}
                    onChange={(_, data) => setField('description', data.value)}
                    placeholder="What does this workflow do, and when would someone use it?"
                    resize="vertical"
                    aria-label="Recipe description"
                  />
                </Field>
                <Field label="Category">
                  <Select
                    value={fields.category}
                    onChange={(e) => setField('category', e.target.value)}
                    aria-label="Recipe category"
                  >
                    {TEMPLATE_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_DISPLAY[cat] ?? cat}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Tags" hint="Comma-separated, used for gallery search">
                  <Input
                    value={fields.tags}
                    onChange={(_, data) => setField('tags', data.value)}
                    placeholder="e.g. email, approval, teams"
                    aria-label="Recipe tags"
                  />
                </Field>
                <Field label="Author" required>
                  <Input
                    value={fields.author}
                    onChange={(_, data) => setField('author', data.value)}
                    placeholder="Your name or team"
                    aria-label="Recipe author"
                  />
                </Field>
                {connectorApiIds.length > 0 && (
                  <Field
                    label="Featured connectors"
                    hint="Selected connectors are shown as icons on the template card"
                  >
                    <div className={styles.connectorList} role="group" aria-label="Featured connectors">
                      {connectorApiIds.map((apiId) => (
                        <FeaturedConnectorCheckbox
                          key={apiId}
                          apiId={apiId}
                          checked={featuredSelection[apiId] !== false}
                          onToggle={handleConnectorToggle}
                          onResolved={handleConnectorResolved}
                        />
                      ))}
                    </div>
                  </Field>
                )}
              </div>
              <div className={styles.footer}>
                <Button appearance="secondary" onClick={onDismiss}>
                  Cancel
                </Button>
                <Button appearance="primary" onClick={handleMetadataNext} disabled={!isMetadataValid}>
                  Next: Review JSON
                </Button>
              </div>
            </>
          )}

          {/* Step 2 — Review & sanitize */}
          {step === 'review' && (
            <>
              <div className={styles.content}>
                <div className={styles.infoBox}>
                  <Text className={styles.infoTitle}>
                    <ShieldCheckmark16Regular aria-hidden="true" />
                    Automatic sanitization already applied
                  </Text>
                  <Text className={styles.infoText}>
                    Credential headers (authorization, cookies, API keys) and SAS query
                    parameters were redacted from the trigger sample, infrastructure
                    headers were removed, and response headers were stripped from action
                    mocks. Still, review the JSON below for anything the scrubber can't
                    know about — names, email addresses, internal URLs, or sample data
                    you don't want to share — and edit it out before continuing.
                  </Text>
                </div>
                <div className={styles.editorWrapper}>
                  <JsonEditor
                    value={jsonText}
                    onChange={handleJsonChange}
                    fill
                    hasError={jsonErrors.length > 0}
                  />
                </div>
                {jsonErrors.length > 0 && (
                  <div className={styles.errorBox} role="alert">
                    <Text className={styles.errorTitle}>
                      <ErrorCircle16Regular aria-hidden="true" />
                      The edited JSON is not a valid recipe
                    </Text>
                    {jsonErrors.map((err, i) => (
                      <Text key={i} className={styles.errorItem}>
                        {err}
                      </Text>
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.footer}>
                <Button appearance="secondary" onClick={handleReviewBack}>
                  Back
                </Button>
                <Button appearance="primary" onClick={handleReviewNext}>
                  Next: Download
                </Button>
              </div>
            </>
          )}

          {/* Step 3 — Download */}
          {step === 'download' && finalRecipe && (
            <>
              <div className={styles.content}>
                <Text className={styles.hint}>
                  Your recipe is validated and ready to share.
                </Text>
                <div className={styles.downloadRow}>
                  <Button
                    appearance="primary"
                    icon={<ArrowDownload16Regular />}
                    onClick={handleDownload}
                  >
                    Download
                  </Button>
                  <Text className={styles.fileName}>{fileName}</Text>
                </div>
                {hasDownloaded && (
                  <Text className={styles.downloadedNote}>
                    <CheckmarkCircle16Regular aria-hidden="true" />
                    Downloaded — you can download again if needed.
                  </Text>
                )}
                <div className={styles.nextSteps}>
                  <Text weight="semibold">Next steps</Text>
                  <ol className={styles.nextStepsList}>
                    <li>
                      Test it: open the Templates gallery, choose the{' '}
                      <strong>Import from JSON</strong> card, and load this file to preview
                      and apply it like any other template.
                    </li>
                    <li>
                      Share it: open a pull request to{' '}
                      <a href={PUBLIC_RECIPES_REPO_URL} target="_blank" rel="noreferrer">
                        Azure/ProjectAutoRecipes
                      </a>{' '}
                      that adds this file as{' '}
                      <strong>recipes/{finalRecipe.metadata.id}/recipe.json</strong> and
                      appends <strong>{finalRecipe.metadata.id}</strong> to{' '}
                      <strong>recipes/manifest.json</strong>. The repo&apos;s CI validates
                      the recipe automatically; once merged, it becomes available to
                      everyone.
                    </li>
                  </ol>
                </div>
              </div>
              <div className={styles.footer}>
                <Button appearance="secondary" onClick={() => setStep('review')}>
                  Back
                </Button>
                <Button appearance="primary" onClick={onDismiss}>
                  Done
                </Button>
              </div>
            </>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

CreateRecipeDialog.displayName = 'CreateRecipeDialog';
